/**
 * The auth-surface shapes, projected from the generated REST types.
 *
 * Named aliases rather than re-declarations: `rest.d.ts` is generated from
 * `spec/openapi.yaml` on every `npm run gen`, so a renamed wire field breaks the
 * build here instead of at runtime. Nothing in this file may restate a field
 * name that the spec already carries.
 */

import type { components } from '@/api/generated/rest'

export type NonceResponse = components['schemas']['NonceResponse']
export type SiweRequest = components['schemas']['SiweRequest']
export type Session = components['schemas']['Session']
export type SessionKey = components['schemas']['SessionKey']
export type SessionKeyRequest = components['schemas']['SessionKeyRequest']

/**
 * A `Session` known to belong to an account.
 *
 * `Session.authenticated` is the only required property on the schema, because
 * the anonymous response carries nothing else (docs/06 §F21a). Everything the UI
 * wants to render — the address, the expiry, the key count — is optional on the
 * generated type as a result. Narrowing once here means the header and the
 * ticket read non-optional fields instead of each defending itself.
 */
export type AuthenticatedSession = Session & {
  authenticated: true
  address: NonNullable<Session['address']>
  chain_id: NonNullable<Session['chain_id']>
}

export function isAuthenticatedSession(
  session: Session | null | undefined,
): session is AuthenticatedSession {
  return Boolean(
    session?.authenticated &&
      typeof session.address === 'string' &&
      session.address.length > 0 &&
      typeof session.chain_id === 'number',
  )
}

/** `status ∈ {ACTIVE, EXPIRED, REVOKED}` — `openapi`'s `SessionKey.status`. */
export type SessionKeyStatus = NonNullable<SessionKey['status']>

/**
 * A key is usable for signing only while it is `ACTIVE` *and* has real time
 * left. The 120-second floor is docs/03 §5.2: a key expiring inside the next two
 * minutes is dropped rather than used, because a key that dies mid-market is a
 * rejection the user cannot act on before the market ends.
 */
export const KEY_MIN_REMAINING_MS = 120_000
